import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Menu from '../Menu';
import PageHeader from '../PageHeader';
import GlobalVariable from '../../GlobalVariable';
import api from '../../api';
import Comments from './Comments';
import {learningCount} from './learning';
import './Learning.scss';

type LessonData = {
    title: string;
    content: string;
    categoryTitle: string | null;
    categoryId: number | null;
    time: string | null;
    rewardCoins: number | null;
    questions: {id: number}[];
};

type Attempt = {
    attemptId: number;
    startedAt: string;
    isFinished: boolean;
    answersCount: number;
    correctCount: number;
};

export const convertLessonVideo = (html: string) => (html || '').replace(
    /<iframe[^>]+src="([^"]+\.mp4)"[^>]*><\/iframe>/g,
    (_match, src) => `<video class="ql-video" controls playsinline preload="metadata"><source src="${src}" type="video/mp4" />Ваш браузер не поддерживает видео.</video>`
);

export function Lesson({idLesson, idCategory}: {idLesson: string | number; idCategory: string | number}) {
    const [lesson, setLesson] = useState<LessonData | null>(null);
    const [attempts, setAttempts] = useState<Attempt[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [commentsOpen, setCommentsOpen] = useState(false);
    const [reload, setReload] = useState(0);

    useEffect(() => {
        let active = true;
        window.scrollTo(0, 0);
        setLoading(true);
        setError('');
        setLesson(null);
        setAttempts([]);
        setCommentsOpen(false);

        api.post(GlobalVariable.staffBack() + '/getLesson/' + idLesson, {}, {withCredentials: true})
            .then(result => {
                if (!active) return;
                setLesson(result.data);
                setLoading(false);
                api.post(GlobalVariable.staffBack() + '/api/lesson/' + idLesson + '/view', {}, {withCredentials: true}).catch(() => {});
            }).catch(() => {
                if (!active) return;
                setError('Не удалось загрузить урок. Попробуйте ещё раз.');
                setLoading(false);
            });

        api.get(GlobalVariable.staffBack() + '/api/lesson/' + idLesson + '/test-attempts', {withCredentials: true})
            .then(result => { if (active && Array.isArray(result.data)) setAttempts(result.data); })
            .catch(() => {});

        return () => { active = false; };
    }, [idLesson, reload]);

    const categoryUrl = `/knowledge-base/${lesson?.categoryId || idCategory}`;
    const testUrl = `${categoryUrl}/lesson/${idLesson}/test`;
    const passed = attempts.some(attempt => attempt.isFinished && attempt.answersCount > 0 && attempt.correctCount / attempt.answersCount >= 0.8);
    const unfinished = attempts.some(attempt => !attempt.isFinished);
    const testLabel = passed ? 'Пройти тест ещё раз' : unfinished ? 'Продолжить тест' : 'Начать тест';

    return <div className='main learning-page learning-lesson-page'>
        <div className='content mx-w-content'>
            <PageHeader pageTitle='Урок' backBtnUrl={categoryUrl}/>
            <div className='pt-header'/>
            <div className='learning-container'>
                {loading && <div className='learning-state' role='status'>Загружаем урок…</div>}
                {error && <div className='learning-state is-error' role='alert'>{error}<button type='button' onClick={() => setReload(value => value + 1)}>Повторить</button></div>}
                {!loading && !error && lesson && <>
                    <header className='learning-lesson-head'>
                        <span className='learning-eyebrow'>{lesson.categoryTitle || 'Обучение'}</span>
                        <h2>{lesson.title}</h2>
                        <div className='learning-lesson-facts'>
                            {lesson.time && <span>◷ {lesson.time}</span>}
                            {lesson.questions.length > 0 && <span>{learningCount(lesson.questions.length, 'вопрос', 'вопроса', 'вопросов')} в тесте</span>}
                            {passed && <span className='is-passed'>Тест пройден</span>}
                        </div>
                    </header>

                    <article className='learning-article' dangerouslySetInnerHTML={{__html: convertLessonVideo(lesson.content)}}/>

                    {lesson.questions.length > 0 && <section className='learning-test-invite'>
                        <div className='learning-test-icon' aria-hidden='true'>✓</div>
                        <div>
                            <h3>Проверьте себя</h3>
                            <p>{learningCount(lesson.questions.length, 'вопрос', 'вопроса', 'вопросов')}{lesson.rewardCoins && !passed ? ` · до ${lesson.rewardCoins} ЧиоКоинов за первый успешный результат` : ''}</p>
                        </div>
                        <Link to={testUrl} className='learning-primary-action'>{testLabel} <span aria-hidden='true'>→</span></Link>
                    </section>}

                    {attempts.length > 0 && <details className='learning-disclosure'>
                        <summary>История попыток <span>{attempts.length} ›</span></summary>
                        <div className='learning-attempts'>{attempts.map(attempt => <div key={attempt.attemptId}>
                            <span>{attempt.startedAt}</span>
                            <strong>{attempt.isFinished ? `${attempt.correctCount} из ${attempt.answersCount}` : 'В процессе'}</strong>
                        </div>)}</div>
                    </details>}

                    <details className='learning-disclosure' onToggle={event => setCommentsOpen((event.currentTarget as HTMLDetailsElement).open)}>
                        <summary>Обсуждение урока <span>›</span></summary>
                        {commentsOpen && <Comments lessonId={Number(idLesson)}/>}
                    </details>
                </>}
            </div>
        </div>
        <Menu/>
    </div>;
}

export default Lesson;
